/**
 * Ventes à arbitrer — arbitrage fiscal manuel (10/06/2026)
 *
 * Liste les ventes statut_fiscal='a_arbitrer' (vue v_ventes_statut_fiscal).
 * Trancher = écrire les colonnes sources via PUT /ventes/<id>/arbitrer :
 *   facture FA-…   → déclaré
 *   BL BL-…        → noir (ou déclaré si BL converti)
 *   noir           → BL sans numéro retrouvable
 *   hors_champ     → perso/famille, exclu du CA
 * La vue recalcule toute seule ; la ligne disparaît de la liste.
 * "Corriger" = édition montant/client/date/note via PUT /ventes/<id> existant (§201).
 * Accès : centrale/admin (l'endpoint backend refuse les autres rôles).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../services/api';
import { ArrowLeft, Scale, Pencil, ChevronDown, ChevronUp, RefreshCw, X, Check } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(parseFloat(n || 0));

export default function VentesArbitrage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHorsChamp, setShowHorsChamp] = useState(false);
  const [classerId, setClasserId] = useState(null);     // vente_id en cours de classement
  const [editerId, setEditerId] = useState(null);       // vente_id en cours d'édition
  const [busy, setBusy] = useState(false);

  // Référentiels pour les autocompletes
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/rapports/ventes-a-arbitrer`);
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Erreur'); return; }
      setData(d);
    } catch (e) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Charger les référentiels une fois (datalists factures + clients)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/factures`);
        const d = await r.json();
        if (r.ok && Array.isArray(d)) setFactures(d.map(f => f.numero_facture).filter(Boolean));
      } catch (e) { /* non bloquant */ }
      try {
        const r = await fetch(`${API_URL}/clients`);
        const d = await r.json();
        if (r.ok && Array.isArray(d)) setClients(d.map(c => ({ id: c.id, nom: c.nom })).filter(c => c.nom));
      } catch (e) { /* non bloquant */ }
    })();
  }, []);

  const arbitrer = async (venteId, action, numero) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/ventes/${venteId}/arbitrer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, numero: numero || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Erreur arbitrage'); return false; }
      setClasserId(null);
      await load();
      return true;
    } catch (e) {
      setError('Erreur réseau');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const corriger = async (venteId, form) => {
    setBusy(true);
    setError('');
    try {
      const body = {};
      if (form.prix_vente !== '') body.prix_vente = parseFloat(form.prix_vente);
      if (form.date_vente) body.date_vente = form.date_vente;
      if (form.commentaire !== '') body.commentaire = form.commentaire;
      if (form.client_nom) {
        const match = clients.find(c => c.nom.toLowerCase() === form.client_nom.trim().toLowerCase());
        if (!match) { setError(`Client "${form.client_nom}" introuvable — choisir dans la liste`); setBusy(false); return false; }
        body.client_id = match.id;
      }
      const res = await fetch(`${API_URL}/ventes/${venteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Erreur édition'); return false; }
      setEditerId(null);
      await load();
      return true;
    } catch (e) {
      setError('Erreur réseau');
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-20"><RefreshCw className="h-8 w-8 animate-spin text-gray-300" /></div>;
  }

  const aArbitrer = data?.a_arbitrer || [];
  const horsChamp = data?.hors_champ || [];
  const t = data?.totaux || {};

  return (
    <div className="space-y-5 max-w-6xl mx-auto p-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/rapport-financier')}
                  className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50" title="Retour rapport financier">
            <ArrowLeft className="h-4 w-4 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Scale className="h-6 w-6 text-amber-600" /> Ventes à arbitrer
            </h1>
            <p className="text-sm text-gray-500">
              {t.nb_a_arbitrer || 0} vente{(t.nb_a_arbitrer || 0) > 1 ? 's' : ''} · {fmt(t.ca_a_arbitrer)} DH —
              classer chaque vente pour fiabiliser le déclaré / noir
            </p>
          </div>
        </div>
        <button onClick={load} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-sm">{error}</div>}

      {/* Aide : rôle des deux boutons */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900 leading-relaxed">
        💡 <strong>Classer</strong> = définir le statut fiscal de la vente : <em>déclarée</em> si rattachée
        à une facture, <em>noir</em> si BL non converti, ou <em>hors champ</em> pour un cadeau/perso
        (exclu du CA). Ça ne crée rien — ça range la vente existante.
        &nbsp;·&nbsp; <strong>Corriger</strong> = modifier les infos de la vente (montant, client, date, note)
        si elles sont erronées.
      </div>

      {/* Liste à arbitrer */}
      <div className="bg-amber-50/60 border-2 border-amber-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 bg-amber-100/70 border-b border-amber-200 text-sm font-bold text-amber-900">
          ⚠ À arbitrer ({aArbitrer.length})
        </div>
        {aArbitrer.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">🎉 Plus rien à arbitrer.</div>
        ) : (
          <div className="divide-y divide-amber-100">
            {aArbitrer.map(v => (
              <LigneArbitrage key={v.vente_id} v={v}
                isClasser={classerId === v.vente_id}
                isEditer={editerId === v.vente_id}
                onClasser={() => { setClasserId(classerId === v.vente_id ? null : v.vente_id); setEditerId(null); }}
                onEditer={() => { setEditerId(editerId === v.vente_id ? null : v.vente_id); setClasserId(null); }}
                arbitrer={arbitrer} corriger={corriger} busy={busy} factures={factures} clients={clients} />
            ))}
          </div>
        )}
      </div>

      {/* Hors champ — repliable */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <button onClick={() => setShowHorsChamp(!showHorsChamp)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50">
          <span className="text-sm font-bold text-gray-700">
            Hors champ — exclu du CA ({horsChamp.length} · {fmt(t.ca_hors_champ)} DH)
          </span>
          {showHorsChamp ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>
        {showHorsChamp && (
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {horsChamp.length === 0 && <div className="p-4 text-sm text-gray-400">Aucune vente hors champ.</div>}
            {horsChamp.map(v => (
              <div key={v.vente_id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono font-semibold text-gray-800">{v.reference_origine}</span>
                  <span className="ml-2 text-gray-600">{v.bijou_nom}</span>
                  <span className="ml-2 text-gray-400">{v.client_nom || '—'}</span>
                  {v.doc_annotation && <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{v.doc_annotation}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{fmt(v.prix_vente)} DH</span>
                  <button onClick={() => arbitrer(v.vente_id, 'reset')}
                          disabled={busy}
                          className="text-xs text-blue-600 hover:underline">Remettre à arbitrer</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LigneArbitrage({ v, isClasser, isEditer, onClasser, onEditer, arbitrer, corriger, busy, factures, clients }) {
  const [numFacture, setNumFacture] = useState('');
  const [numBl, setNumBl] = useState('');
  const [form, setForm] = useState({
    prix_vente: v.prix_vente != null ? String(v.prix_vente) : '',
    date_vente: v.date_vente ? String(v.date_vente).slice(0, 10) : '',
    commentaire: v.commentaire || '',
    client_nom: '',
  });

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className="font-mono font-semibold text-gray-900">{v.reference_origine || v.id_produit}</span>
          <span className="ml-2 text-gray-700">{v.bijou_nom}</span>
          <div className="text-xs text-gray-500 mt-0.5">
            {v.client_nom || 'Client inconnu'} · {v.date_vente ? String(v.date_vente).slice(0, 10) : 'date ?'}
            {v.doc_annotation && (
              <span className="ml-2 bg-stone-200 text-stone-700 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                    title="Annotation d'origine (fichier Excel)">
                📝 {v.doc_annotation}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-gray-900 whitespace-nowrap">{fmt(v.prix_vente)} DH</span>
          <button onClick={onEditer}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${isEditer ? 'bg-blue-600 text-white border-blue-600' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}>
            <Pencil className="h-3 w-3 inline mr-1" />Corriger
          </button>
          <button onClick={onClasser}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${isClasser ? 'bg-amber-600 text-white border-amber-600' : 'border-amber-400 text-amber-800 hover:bg-amber-100'}`}>
            <Scale className="h-3 w-3 inline mr-1" />Classer
          </button>
        </div>
      </div>

      {/* Panneau Classer */}
      {isClasser && (
        <div className="mt-3 bg-white border border-amber-200 rounded-xl p-4 grid gap-3 sm:grid-cols-2">
          <div className="flex gap-2">
            <input list={`fa-${v.vente_id}`} value={numFacture} onChange={e => setNumFacture(e.target.value)}
                   placeholder="FA-2026/000123"
                   className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            <datalist id={`fa-${v.vente_id}`}>
              {factures.map(n => <option key={n} value={n} />)}
            </datalist>
            <button disabled={busy || !numFacture}
                    onClick={() => arbitrer(v.vente_id, 'facture', numFacture)}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold disabled:opacity-40">
              <Check className="h-3 w-3 inline mr-1" />Facture → déclaré
            </button>
          </div>
          <div className="flex gap-2">
            <input value={numBl} onChange={e => setNumBl(e.target.value)}
                   placeholder="BL-2026/000045"
                   className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            <button disabled={busy || !numBl}
                    onClick={() => arbitrer(v.vente_id, 'bl', numBl)}
                    className="px-3 py-2 bg-stone-700 text-white rounded-lg text-xs font-semibold disabled:opacity-40">
              <Check className="h-3 w-3 inline mr-1" />BL
            </button>
          </div>
          <div className="sm:col-span-2 flex gap-2 flex-wrap">
            <button disabled={busy} onClick={() => arbitrer(v.vente_id, 'noir')}
                    className="px-3 py-2 bg-stone-800 text-white rounded-lg text-xs font-semibold disabled:opacity-40">
              Noir (BL sans numéro retrouvable)
            </button>
            <button disabled={busy} onClick={() => arbitrer(v.vente_id, 'hors_champ')}
                    className="px-3 py-2 bg-gray-400 text-white rounded-lg text-xs font-semibold disabled:opacity-40">
              Hors champ (perso / famille — exclu du CA)
            </button>
            <button onClick={onClasser}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-xs text-gray-600">
              <X className="h-3 w-3 inline mr-1" />Laisser en attente
            </button>
          </div>
        </div>
      )}

      {/* Panneau Corriger */}
      {isEditer && (
        <div className="mt-3 bg-white border border-blue-200 rounded-xl p-4 flex gap-3 flex-wrap items-end">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Montant (DH)</label>
            <input type="number" step="0.01" value={form.prix_vente}
                   onChange={e => setForm(f => ({ ...f, prix_vente: e.target.value }))}
                   className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Date de vente</label>
            <input type="date" value={form.date_vente}
                   onChange={e => setForm(f => ({ ...f, date_vente: e.target.value }))}
                   className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Client (actuel : {v.client_nom || '—'})</label>
            <input list={`cli-${v.vente_id}`} value={form.client_nom}
                   onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))}
                   placeholder="Inchangé si vide"
                   className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-48" />
            <datalist id={`cli-${v.vente_id}`}>
              {clients.map(c => <option key={c.id} value={c.nom} />)}
            </datalist>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 block mb-1">Note (clarification)</label>
            <input value={form.commentaire}
                   onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
                   placeholder="Ex : montant corrigé, à vérifier avec…"
                   className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          </div>
          <button disabled={busy} onClick={() => corriger(v.vente_id, form)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold disabled:opacity-40">
            <Check className="h-3 w-3 inline mr-1" />Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}
